import {
  Control,
  Controller,
  FieldErrors,
  UseFormClearErrors,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form';

import { DecomPolicyFormValues } from '../decom-policy-form-layout.tsx';
import { VaultMatterModel } from '../../../../../api/vault-api.ts';
import VaultMatterHeader from './shared/vault-matter-header.tsx';
import DecomNotifyUsersInput from './shared/decom-notify-users-input.tsx';
import DecomReportRecipientInput from './shared/decom-report-recipient-input.tsx';
import VaultTooltipWithLink from './shared/vault-tooltip-with-link.tsx';
import VaultExportConfigSkeleton from './shared/vault-export-config-skeleton.tsx';
import VaultDatePicker from './shared/vault-date-picker.tsx';
import VaultTimezoneSelect from './shared/vault-timezone-select.tsx';
import VaultDateRangePicker from './shared/vault-date-range-picker.tsx';
import { DestUser, VAULT_DATA_SCOPE_OPTIONS } from '../../../../../../types.ts';

import { ActionErrorEntry } from '../decom-policy-utils.tsx';

import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';

export interface VaultDriveExportConfigProps {
  matter?: VaultMatterModel;
  isLoading?: boolean;
  day: number;
  idx: number;
  watch: UseFormWatch<DecomPolicyFormValues>;
  setValue: UseFormSetValue<DecomPolicyFormValues>;
  control: Control<DecomPolicyFormValues>;
  errors: FieldErrors<DecomPolicyFormValues>;
  clearErrors: UseFormClearErrors<DecomPolicyFormValues>;
  setActionTypesWithErrors: React.Dispatch<
    React.SetStateAction<ActionErrorEntry[]>
  >;
  destUserFields: (DestUser & { id: string })[];
  destUserAppend: (value: DestUser) => void;
  destUserRemove: (index?: number) => void;
  destUserUpdate: (index: number, value: DestUser) => void;
}

export default function VaultDriveExportConfigOld({
  matter,
  isLoading,
  day,
  idx,
  watch,
  setValue,
  control,
  errors,
  clearErrors,
  setActionTypesWithErrors,
  destUserFields,
  destUserAppend,
  destUserRemove,
  destUserUpdate,
}: VaultDriveExportConfigProps) {
  const handleBack = () => {
    setValue(`actions.${day}.actions.${idx}.matterId`, '', {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  if (isLoading) return <VaultExportConfigSkeleton onBack={handleBack} />;

  if (!matter) return null;

  return (
    <Stack gap={3} sx={{ mt: 1 }}>
      <VaultMatterHeader matterName={matter.name} onBack={handleBack} />

      <Stack direction="row" gap={4}>
        <Controller
          name={`actions.${day}.actions.${idx}.dataScope`}
          control={control}
          defaultValue="ALL_DATA"
          render={({ field }) => (
            <FormControl sx={{ minWidth: 350 }} size="small">
              <Typography variant="subtitle2" gutterBottom>
                Source
              </Typography>
              <Select {...field} value={field.value || ''}>
                {VAULT_DATA_SCOPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
      </Stack>

      <Box>
        <Controller
          name={`actions.${day}.actions.${idx}.vaultSharedDrivesOption`}
          control={control}
          defaultValue="NOT_INCLUDED"
          render={({ field }) => (
            <RadioGroup {...field} value={field.value || 'NOT_INCLUDED'}>
              <FormControlLabel
                value="INCLUDED"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">Include Shared Drives</Typography>
                }
              />
              <FormControlLabel
                value="NOT_INCLUDED"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">
                    Do Not Include Shared Drives
                  </Typography>
                }
              />
              <FormControlLabel
                value="INCLUDED_IF_ACCOUNT_IS_NOT_A_MEMBER"
                control={<Radio size="small" />}
                label={
                  <Typography variant="body2">
                    Include only if documents shared directly (not due to shared
                    drive membership)
                  </Typography>
                }
              />
            </RadioGroup>
          )}
        />
      </Box>

      <Stack direction="row" gap={4} flexWrap="wrap">
        <VaultTimezoneSelect day={day} idx={idx} control={control} />

        <VaultDateRangePicker
          day={day}
          idx={idx}
          control={control}
          errors={errors}
          label="Date Modified (optional)"
        />

        <Box>
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography variant="subtitle2" gutterBottom>
              Version Date (optional)
            </Typography>
            <VaultTooltipWithLink
              preset="driveVersionDate"
              sxIcon={{ mb: 0.5 }}
            />
          </Stack>
          <VaultDatePicker
            day={day}
            idx={idx}
            name={`actions.${day}.actions.${idx}.vaultVersionDate`}
            label="Version Date"
            control={control}
            errors={errors}
          />
        </Box>
      </Stack>

      <Box>
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography variant="subtitle2" gutterBottom>
            Terms (optional)
          </Typography>
          <VaultTooltipWithLink preset="vaultQueryTerms" sxIcon={{ mb: 0.5 }} />
        </Stack>
        <Controller
          name={`actions.${day}.actions.${idx}.terms`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              value={field.value || ''}
              placeholder="Query Terms"
              helperText="Example: from:username1 subject: Hello has:attachment"
              size="small"
              fullWidth
              variant="standard"
            />
          )}
        />
      </Box>

      <Divider sx={{ my: 1 }} />

      <DecomNotifyUsersInput
        day={day}
        idx={idx}
        control={control}
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
      />

      <DecomReportRecipientInput
        day={day}
        idx={idx}
        control={control}
        watch={watch}
        setValue={setValue}
        clearErrors={clearErrors}
        errors={errors}
        actionType="vaultDriveExport"
        setActionTypesWithErrors={setActionTypesWithErrors}
        destUserFields={destUserFields}
        destUserAppend={destUserAppend}
        destUserRemove={destUserRemove}
        destUserUpdate={destUserUpdate}
      />
    </Stack>
  );
}
